import React, {useEffect, useState} from "react";

import {Icon} from "../../AvailableJobsPage.styles";
import {Shift, ShiftHeader, ShiftsContainer, SmallBox} from "./Shifts.styles";

import heartFilled from 'images/heartFilled.svg'
import heartUnfilled from 'images/heartUnfilled.svg'
import instabook from 'images/job-option/instabook.png'
import friendlyEndorsed from 'images/job-option/friendlyEndorsed.png'
import equipmentEndorsed from 'images/job-option/equipmentEndorsed.png'
import {formatDate, getDaysFromDate} from "../../../../../utils";
import { HygienistProps, JobProps, PositionProps } from "utils/types";
import {config} from "api";
import {usePersistedState} from "../../../../../hooks";
import entities from "../../../../../reduxStore/entityKeys";
import Popup from "../../../../../components/Popup";
import useActionApi from "../../../../../hooks/useActionApi";
import Api from "../../../../../api";
import {PopupContainer, PriorityShiftContainer} from "../../../../../components/EditJob/common.styles";
import styles from "../../../../dentist/PostJobPage/PostJob.module.scss";
import {CrossButtonBlack} from "../../../../../components/Buttons/CrossButton";
import clsx from "clsx";
import DefaultButton from "../../../../../components/Buttons/DefaultButton";
import {useHistory} from "react-router";
import FixedLoader from "../../../../../components/FixedLoader";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ShiftsProps {
    availableJobs: JobProps[],
    goToPage: (id: number, filter: any, isFavourite: boolean) => void,
    updateFavourite: (job: JobProps) => void,
    errorGetJos: string,
    updating: boolean,
}


const proposedRate = (hygienist: HygienistProps, rate: number) => {
    let min = 5;
    let max = 6;
    if(hygienist.status === 1){
        max += 3
    }
    if(hygienist.status === 2){
        max += 6
    }
    return `$${rate-min}/hr - $${rate+max}/hr`
}


const DataBox = ({title, data}: {title: string, data: string | any}): JSX.Element => {
    return <SmallBox className={'col'}><span className={'title'}>{title}</span>{data}</SmallBox>
}

const distance = (job: JobProps) => {
    return <>{job.distanceObj?.distance?.text}<br/><span style={{fontSize: '0.8rem'}}>{job.distanceObj?.duration && `(approx. ${job.distanceObj?.duration?.text})`}</span></>
}

interface EnhancedProfileModalProps {
    resumeExpDate: Date | undefined;
    onLater: () => void;
    onClose: () => void;
    onDoItNow: () => void;
}

const EnhancedProfileModal: React.FC<EnhancedProfileModalProps> = ({ resumeExpDate,  onLater, onClose, onDoItNow }) => {
    const isCanBePostponed = resumeExpDate ? (resumeExpDate > new Date()) : true;
    return (
        <PopupContainer>
            <div className={styles.titleContainer}>
                <div className={styles.popupTitle}>Enhanced Profile Required</div>
                <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)} onClick={onClose}/>
            </div>
            <div >
                <p>We are moving away from resumes to a more enhanced user profile. Please complete your enhanced profile.</p>
            </div>
            <div>
                <DefaultButton variant="contained" color="primary" onClick={onDoItNow}>Do it now</DefaultButton>
                {isCanBePostponed ? <DefaultButton variant="contained" onClick={onLater}>Later</DefaultButton> : null}
            </div>
        </PopupContainer>
    );
};

interface WorkExperienceModalProps {
    onClose: () => void;
}

const WorkExperienceModal: React.FC<WorkExperienceModalProps> = ({ onClose }) => {
    const history = useHistory();
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];

    return (
        <PopupContainer>
            <div className={styles.titleContainer}>
                <div className={styles.popupTitle}>{`Heads up, ${hygienist?.firstName}`}</div>
                <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)} onClick={onClose}/>
            </div>
            <div>
                <p>
                    The response to the enhanced profiles has been great, although we have received consistent feedback that dental offices need to see Work Experience in temping shift offers, so we've made this a required section of the profiles. <br/><br/>
                    It looks like you don't have yours filled out just yet - so head on over there to fill it out.
                </p>
            </div>
            <div>
                <DefaultButton 
                    variant="contained" 
                    color="primary" 
                    className={styles.modalButton}
                    onClick={() => history.push('hygienistProfile', { isOpenPositionModal: true })}
                >
                    Fill Out Work Experience
                </DefaultButton>
                <DefaultButton 
                    variant="contained"
                    className={styles.modalButton}
                    onClick={onClose}
                >
                    I'll do this later
                </DefaultButton>
            </div>
        </PopupContainer>
    );
};

const Shifts = ({availableJobs, goToPage, errorGetJos, updateFavourite, updating}: ShiftsProps): JSX.Element => {
    const history = useHistory();
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];
    const [isEnhancedProfileModalOpen, setEnhancedProfileModalOpen] = useState<boolean>(false);
    const [isWorkExperienceModalOpen, setWorkExperienceModalOpen] = useState<boolean>(false);
    const [workExperience, setWorkExperience] = useState<PositionProps[]>([]);
    const [resumeExpDate, setResumeExpDate] = useState<Date | undefined>();
    const [jobId, setJobId] = useState<number>();
    const [filters, setFilters] = useState<{ date: string }>();
    const [favouriteHygienistLocations, setFavouriteHygienistLocations] = useState<number[]>([]);

    const [getResumesExpirationDateLoading, , getResumesExpirationDate] = useActionApi({
        apiMethod: Api.getResumesExpirationDate,
        onSuccess: (resumeExpDate: string) => {
            setResumeExpDate(new Date(resumeExpDate))
        },
    });

    const [getHygienistWorkExperienceLoading, , getHygienistWorkExperience] = useActionApi({
        apiMethod: Api.getHygienistWorkExperience,
        onSuccess: (res: PositionProps[]) => {
            setWorkExperience(res)
        },
    });

    const [checkFavouriteHygienistLocationsLoading, , checkFavouriteHygienistLocations] = useActionApi({
        apiMethod: Api.checkFavouriteHygienistLocations,
        onSuccess: (res: { isFavouriteHygienist: number[] }) => {
            setFavouriteHygienistLocations(res.isFavouriteHygienist)
        },
    });

    const onUpdateProfileLater = () => {
        setEnhancedProfileModalOpen(false);
        if (jobId && filters) {
            const job = availableJobs.find(job => job.id === jobId);
            return goToPage(jobId, filters, job ? isFavouriteHygienist(job) : false);
        }
    }

    const openModal = async (jobId: number, filters: any) => {
        setJobId(jobId);
        setFilters(filters);

        if (hygienist?.skills?.length === 0) {
            return setEnhancedProfileModalOpen(true);
        } else if(!workExperience || workExperience?.length === 0){
            return setWorkExperienceModalOpen(true);
        } else {
            const job = availableJobs.find(job => job.id === jobId);
            return goToPage(jobId, filters, job ? isFavouriteHygienist(job) : false);
        }
    }

    const isFavouriteHygienist = (job: JobProps) => {
        return favouriteHygienistLocations.includes(job.locationId);
    }

    useEffect(() => {
        getResumesExpirationDate({})
        getHygienistWorkExperience(hygienist.id)
    }, []);

    useEffect(() => {
        checkFavouriteHygienistLocations(availableJobs.map(job => job.locationId))
    }, [availableJobs])

    return (
        <ShiftsContainer>
            <Popup opened={isEnhancedProfileModalOpen} onClose={() => setEnhancedProfileModalOpen(false)}>
                <EnhancedProfileModal
                    resumeExpDate={resumeExpDate}
                    onClose={() => setEnhancedProfileModalOpen(false)}
                    onLater={onUpdateProfileLater}
                    onDoItNow={() => {
                        setEnhancedProfileModalOpen(false);
                        history.push('hygienistProfile');
                    }}
                />
            </Popup>
            <Popup opened={isWorkExperienceModalOpen} onClose={() => setWorkExperienceModalOpen(false)}>
                <WorkExperienceModal
                    onClose={() => setWorkExperienceModalOpen(false)}
                />
            </Popup>
            {availableJobs.length ? availableJobs.map((job: JobProps, index) => {
                const {location, shifts, postedOn} = job;
                const isGuaranteedRate = !!job?.ghr
                const shift = shifts[0];
                const days = getDaysFromDate(postedOn)
                let postedOnDays = `on ${formatDate(postedOn, 'dddd MMMM D')}`
                if (days < 1) {
                    postedOnDays = ' Today'
                }
                if (days >= 1 && days <= 7) {
                    postedOnDays = `: ${days}d ago`
                }

                return (
                    <Shift
                        key={index}
                    >
                        <Icon
                            style={{ width: 22, position: 'absolute', display: 'flex', alignSelf: 'flex-end' }}
                            src={job.isFavouriteDentist ? heartFilled : heartUnfilled}
                            onClick={() => updating ? null : updateFavourite(job)}
                        />
                        <div onClick={() => openModal(job.id, { date: shift.shiftDate })}>
                            <ShiftHeader className={'row'}>
                                <div className={'col'}>
                                    <div className={'row'}>
                                        <div>{location?.city}, {location?.province}</div>
                                        {job?.reposted ? <PriorityShiftContainer><ErrorOutlineIcon /> Urgent Shift</PriorityShiftContainer> : null}
                                    </div>
                                    <div>{
                                        isGuaranteedRate
                                            ? `$${job.ghr}/hr Guaranteed`
                                            : proposedRate(hygienist, job.proposedRate || job.avgRate || job.hourlyRate)
                                    }</div>
                                </div>
                            </ShiftHeader>
                            <div className='col'>
                                <div className='row'>
                                    <div className={'half'}>
                                        <DataBox title={"Hours"} data={`${shift?.postedStart} - ${shift?.postedEnd}`}/>
                                    </div>
                                    <div className={'half'}>
                                        <DataBox title={"Shift Date"} data={formatDate(shift.shiftDate, 'dddd MMMM D')}/>
                                    </div>
                                </div>
                                <div className='row'>
                                    {job.type ? null : <div className={'half'}>
                                        <DataBox title={"Recall time"} data={parseInt(location.avgApptTime) + ' min'}/>
                                    </div>}
                                    <div className={'half'}>
                                        <DataBox title={"Distance"} data={distance(job)}/>
                                    </div>
                                </div>
                            </div>
                            <div className='row statuses'>
                                {(!!job.Instabook && (hygienist.status > 0 || isFavouriteHygienist(job))) && <div className='status'>
                                    <Icon
                                        style={{ width: 24 }}
                                        src={instabook}
                                    /> You can instantly book these shift.
                                </div>}
                                {/*{!!location.vaxRequired && <div className='status'>*/}
                                {/*    <Icon*/}
                                {/*        style={{ width: 18 }}*/}
                                {/*        src={vax}*/}
                                {/*    /> Requires Proof of Full Vaccination.*/}
                                {/*</div>}*/}
                                {!!location.friendlyEndorsed && <div className='status'>
                                    <Icon
                                        style={{ width: 18 }}
                                        src={friendlyEndorsed}
                                    /> A friendly office.
                                </div>}
                                {!!location.equipEndorsed && <div className='status'>
                                    <Icon
                                        style={{ width: 18 }}
                                        src={equipmentEndorsed}
                                    /> Great instruments.
                                </div>}
                            </div>
                            <div className='row postedOn'>
                                {`Shift posted ${postedOnDays}`}
                            </div>
                        </div>
                    </Shift>
                )
            }) : <div className={'noData'}>{updating || getResumesExpirationDateLoading ? '' : errorGetJos || 'No shifts'}</div>}

            <FixedLoader loading={updating || getResumesExpirationDateLoading || getHygienistWorkExperienceLoading || checkFavouriteHygienistLocationsLoading}/>
        </ShiftsContainer>)
}

export default Shifts;
